import { useEffect, useState } from "react";
import { ArrowRight, CheckCircle2, Flame, Scale, Loader2, Lock, Zap, Trophy } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ExitIntentModal from "@/components/ExitIntentModal";
import FoundingShareCard from "@/components/FoundingShareCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLES = [
    { value: "law_student", label: "Law Student" },
    { value: "new_attorney", label: "New Attorney (0-3 yrs)" },
    { value: "seasoned_attorney", label: "Seasoned Attorney (4+ yrs)" },
    { value: "prosecutor", label: "Prosecutor / DA" },
    { value: "public_defender", label: "Public Defender" },
    { value: "other", label: "Other Legal Professional" },
];

const TIERS = [
    {
        key: "solo",
        label: "SOLO",
        price: 49,
        unit: "/MO",
        bullets: [
            "10 cases / month",
            "AI opposing counsel + judge",
            "Difficulty: 🟢 to 🟠",
            "Graded transcripts",
        ],
    },
    {
        key: "pro",
        label: "PRO",
        price: 149,
        unit: "/MO",
        bullets: [
            "UNLIMITED cases",
            "Full 10-bot adversarial system",
            "Difficulty: 🟢 to 🔴 Capital",
            "CLE-credit certificates*",
            "Custom case upload",
        ],
        featured: true,
    },
    {
        key: "firm",
        label: "FIRM",
        price: 499,
        unit: "/MO",
        bullets: [
            "5 attorney seats",
            "Team dashboards + comparison",
            "Mock-trial scrimmage mode",
            "Onboarding for new associates",
            "Priority support",
        ],
    },
];

export default function Courtroom() {
    const [stats, setStats] = useState(null);
    const [recent, setRecent] = useState([]);
    const [form, setForm] = useState({
        email: "",
        name: "",
        role: "",
        state: "",
        practice_area: "",
    });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [signupResult, setSignupResult] = useState(null);
    const [referredBy, setReferredBy] = useState(null);
    const [presaleBusy, setPresaleBusy] = useState(false);

    useEffect(() => {
        // Capture ?ref=<spot_number> on first paint, persist for the session
        // so signups + checkouts both carry the attribution.
        try {
            const params = new URLSearchParams(window.location.search);
            const raw = params.get("ref");
            if (raw) {
                const n = parseInt(raw, 10);
                if (Number.isFinite(n) && n > 0 && n < 1_000_000) {
                    setReferredBy(n);
                    sessionStorage.setItem("hpm_courtroom_ref", String(n));
                    return;
                }
            }
            const cached = sessionStorage.getItem("hpm_courtroom_ref");
            if (cached) {
                const n = parseInt(cached, 10);
                if (Number.isFinite(n) && n > 0) setReferredBy(n);
            }
        } catch {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        axios
            .get(`${API}/courtroom/stats`)
            .then((r) => setStats(r.data))
            .catch(() => setStats(null));
    }, []);

    useEffect(() => {
        let cancelled = false;
        const load = () =>
            axios
                .get(`${API}/courtroom/recent?limit=3`)
                .then((r) => {
                    if (!cancelled) setRecent(r.data?.items || []);
                })
                .catch(() => {});
        load();
        const id = setInterval(load, 30000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, []);

    const submitWaitlist = async (e) => {
        e?.preventDefault?.();
        if (!form.email.includes("@")) {
            toast.error("Enter a valid email");
            return;
        }
        setBusy(true);
        try {
            const r = await axios.post(`${API}/courtroom/waitlist`, {
                ...form,
                interest: "waitlist",
                source: "courtroom_landing",
                referred_by: referredBy || undefined,
            });
            setSignupResult(r.data || null);
            setDone(true);
            toast.success("You're on the list. Founding-member pricing locked.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Submission failed");
        } finally {
            setBusy(false);
        }
    };

    const presale = async () => {
        if (!form.email.includes("@")) {
            toast.error("Enter your email first");
            return;
        }
        setPresaleBusy(true);
        try {
            const r = await axios.post(`${API}/courtroom/presale-checkout`, {
                ...form,
                origin_url: window.location.origin,
                plan: "founding",
                referred_by: referredBy || undefined,
            });
            window.location.href = r.data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Checkout failed");
            setPresaleBusy(false);
        }
    };

    const reserve = async () => {
        // Reserve flow removed — $99 founding deal is the only paid validation now.
        await presale();
    };

    return (
        <div className="min-h-screen bg-black text-white" data-testid="courtroom-page">
            <Header />
            <ExitIntentModal alreadyConverted={done} />

            {/* HERO */}
            <section className="border-b-2 border-white/20 relative overflow-hidden">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-16 pb-20">
                    <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-12 items-start">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-4 flex items-center gap-2">
                                <Scale size={12} />
                                COMING SOON · HPM COURTROOM
                            </div>
                            <h1 className="font-anton text-5xl sm:text-7xl uppercase leading-[0.9] mb-6">
                                Quimbee Is For Reading.{" "}
                                <span className="text-[#FF3B30]">HPM Courtroom Is For Trying Cases.</span>
                            </h1>
                            <p className="font-sans text-white/80 text-lg max-w-2xl leading-relaxed mb-6">
                                10 specialized AI bots play the judge, opposing counsel, witnesses,
                                jury, and experts. You pick a case from{" "}
                                <span className="text-[#00FF85]">🟢 traffic stop</span> to{" "}
                                <span className="text-[#FF3B30]">🔴 capital appeal</span>. You go to
                                trial. You get graded. CLE credit pending.
                            </p>
                            <div className="flex flex-wrap items-center gap-4 mb-8 font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
                                <span className="flex items-center gap-1.5">
                                    <Trophy size={11} className="text-[#FFCC00]" /> CLE-CREDIT CERTIFICATES
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <Zap size={11} className="text-[#FF3B30]" /> INSTANT FEEDBACK
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <Lock size={11} className="text-[#00FF85]" /> FOUNDING-MEMBER PRICING
                                </span>
                            </div>

                            {/* VALIDATION STATS */}
                            {stats && (
                                <div
                                    className="border-2 border-white/20 bg-white/5 p-4 flex flex-wrap gap-8 mb-2"
                                    data-testid="courtroom-stats"
                                >
                                    <div>
                                        <div className="font-anton text-3xl text-[#00FF85]">
                                            {stats.total_signups}
                                        </div>
                                        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50">
                                            LAWYERS ON WAITLIST
                                        </div>
                                    </div>
                                    <div>
                                        <div className="font-anton text-3xl text-[#FF3B30]">
                                            {stats.presale_interest}
                                        </div>
                                        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50">
                                            FOUNDING MEMBERS
                                        </div>
                                    </div>
                                    <div>
                                        <div className="font-anton text-3xl text-[#FFCC00]">
                                            $99
                                        </div>
                                        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50">
                                            LIFETIME PRESALE · 50% OFF FOREVER
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* SOCIAL PROOF TICKER — anonymized recent signups */}
                            <div
                                className="mt-4 border border-white/15 bg-white/[0.03] px-4 py-3"
                                data-testid="courtroom-recent-ticker"
                            >
                                {recent.length > 0 ? (
                                    <>
                                        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40 mb-2 flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#00FF85] animate-pulse" />
                                            RECENT SIGNUPS · LIVE
                                        </div>
                                        <ul className="space-y-1.5">
                                            {recent.map((it, i) => (
                                                <li
                                                    key={i}
                                                    className="flex items-center justify-between gap-3 font-mono text-[11px] text-white/80"
                                                    data-testid={`courtroom-recent-item-${i}`}
                                                >
                                                    <span className="truncate">
                                                        <span className="text-white font-semibold">{it.initials}</span>
                                                        <span className="text-white/50"> · </span>
                                                        {it.role_label}
                                                        {it.state && it.state !== "—" && (
                                                            <>
                                                                <span className="text-white/50"> · </span>
                                                                {it.state}
                                                            </>
                                                        )}
                                                    </span>
                                                    <span className="flex items-center gap-2 shrink-0">
                                                        {it.is_presale && (
                                                            <span className="text-[#00FF85] text-[9px] uppercase tracking-[0.2em]">
                                                                $99 PAID
                                                            </span>
                                                        )}
                                                        <span className="text-white/40 text-[10px]">{it.when}</span>
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                ) : (
                                    <div
                                        className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFCC00] flex items-center gap-2"
                                        data-testid="courtroom-recent-empty"
                                    >
                                        <Flame size={11} /> Be founding member #1 — 47% off lifetime
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* WAITLIST FORM */}
                        <div
                            className="border-2 border-[#FF3B30] bg-[#FF3B30]/5 p-6"
                            data-testid="courtroom-waitlist-form"
                        >
                            {referredBy && (
                                <div
                                    className="mb-4 -mt-1 border border-[#00FF85]/40 bg-[#00FF85]/5 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#00FF85]"
                                    data-testid="courtroom-referral-banner"
                                >
                                    ✓ REFERRED BY FOUNDING MEMBER #{referredBy} ·
                                    YOU BOTH UNLOCK PRIORITY ACCESS ON LAUNCH
                                </div>
                            )}
                            {done ? (
                                <div data-testid="courtroom-waitlist-success">
                                    <div className="flex items-center gap-2 mb-3">
                                        <CheckCircle2 size={20} className="text-[#00FF85]" />
                                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FF85]">
                                            YOU'RE IN
                                        </div>
                                    </div>
                                    <div className="font-anton text-2xl uppercase mb-3">
                                        Welcome to the Founding Class.
                                    </div>
                                    <p className="font-sans text-sm text-white/80 mb-4">
                                        We'll email you the moment Courtroom opens. In the meantime,
                                        lock in <span className="text-white">$99 lifetime founding-member pricing</span>{" "}
                                        — pay once, save $50+/mo forever vs. retail.
                                    </p>
                                    <button
                                        onClick={presale}
                                        disabled={presaleBusy}
                                        className="btn-brutal btn-brutal-red w-full justify-center text-[10px]"
                                        data-testid="courtroom-presale-btn"
                                    >
                                        {presaleBusy ? (
                                            <Loader2 size={12} className="animate-spin" />
                                        ) : (
                                            <>
                                                LOCK $99 FOUNDING DEAL → <ArrowRight size={12} />
                                            </>
                                        )}
                                    </button>
                                    <FoundingShareCard
                                        spotNumber={signupResult?.spot_number}
                                        foundingTotal={signupResult?.founding_total || stats?.founding_spots_total || 100}
                                    />
                                </div>
                            ) : (
                                <form onSubmit={submitWaitlist}>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-2 flex items-center gap-2">
                                        <Flame size={11} /> FOUNDING MEMBER · $99 LIFETIME
                                    </div>
                                    <div className="font-anton text-2xl uppercase leading-none mb-3">
                                        Lock $49/mo Solo forever.
                                    </div>
                                    {stats &&
                                        typeof stats.founding_spots_remaining === "number" && (
                                            <div
                                                className="mb-4 border border-[#FFCC00]/40 bg-[#FFCC00]/5 p-3"
                                                data-testid="courtroom-scarcity-meter"
                                            >
                                                <div className="flex items-baseline justify-between mb-2">
                                                    <div className="font-anton text-xl text-[#FFCC00] leading-none">
                                                        {stats.founding_spots_remaining}
                                                        <span className="text-white/60 text-sm">
                                                            {" "}/ {stats.founding_spots_total}
                                                        </span>
                                                    </div>
                                                    <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/60">
                                                        SPOTS LEFT AT $99
                                                    </div>
                                                </div>
                                                <div className="w-full h-1.5 bg-white/10 overflow-hidden">
                                                    <div
                                                        className="h-full bg-[#FFCC00] transition-all duration-700"
                                                        style={{
                                                            width: `${Math.min(
                                                                100,
                                                                Math.round(
                                                                    ((stats.founding_spots_total -
                                                                        stats.founding_spots_remaining) /
                                                                        Math.max(
                                                                            stats.founding_spots_total,
                                                                            1,
                                                                        )) *
                                                                        100,
                                                                ),
                                                            )}%`,
                                                        }}
                                                        data-testid="courtroom-scarcity-fill"
                                                    />
                                                </div>
                                                <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.25em] text-[#FF3B30] flex items-center gap-2">
                                                    <Zap size={10} /> NEXT: {stats.next_price_tier}
                                                </div>
                                            </div>
                                        )}
                                    <p className="font-sans text-sm text-white/70 mb-4 leading-relaxed">
                                        First 100 founding members pay $99 once. Solo tier billed at
                                        $49/mo for life when we launch (retail: $99/mo). Refundable
                                        30 days.
                                    </p>
                                    <input
                                        required
                                        type="email"
                                        placeholder="WORK EMAIL"
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        className="w-full bg-black border-2 border-white/30 p-3 mb-2 font-mono text-xs text-white placeholder-white/40 focus:border-[#FF3B30] outline-none"
                                        data-testid="courtroom-email"
                                    />
                                    <input
                                        type="text"
                                        placeholder="NAME (OPTIONAL)"
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        className="w-full bg-black border-2 border-white/30 p-3 mb-2 font-mono text-xs text-white placeholder-white/40 focus:border-[#FF3B30] outline-none"
                                        data-testid="courtroom-name"
                                    />
                                    <select
                                        value={form.role}
                                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                                        className="w-full bg-black border-2 border-white/30 p-3 mb-2 font-mono text-xs text-white focus:border-[#FF3B30] outline-none"
                                        data-testid="courtroom-role"
                                    >
                                        <option value="">SELECT YOUR ROLE</option>
                                        {ROLES.map((r) => (
                                            <option key={r.value} value={r.value}>
                                                {r.label}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="grid grid-cols-2 gap-2 mb-3">
                                        <input
                                            type="text"
                                            placeholder="STATE"
                                            value={form.state}
                                            onChange={(e) => setForm({ ...form, state: e.target.value })}
                                            className="bg-black border-2 border-white/30 p-3 font-mono text-xs text-white placeholder-white/40 focus:border-[#FF3B30] outline-none"
                                            data-testid="courtroom-state"
                                        />
                                        <input
                                            type="text"
                                            placeholder="PRACTICE AREA"
                                            value={form.practice_area}
                                            onChange={(e) =>
                                                setForm({ ...form, practice_area: e.target.value })
                                            }
                                            className="bg-black border-2 border-white/30 p-3 font-mono text-xs text-white placeholder-white/40 focus:border-[#FF3B30] outline-none"
                                            data-testid="courtroom-practice-area"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={presale}
                                        disabled={presaleBusy}
                                        className="btn-brutal btn-brutal-red w-full justify-center text-[10px]"
                                        data-testid="courtroom-presale-btn"
                                    >
                                        {presaleBusy ? (
                                            <Loader2 size={12} className="animate-spin" />
                                        ) : (
                                            <>
                                                CLAIM FOUNDING DEAL · $99 → <ArrowRight size={12} />
                                            </>
                                        )}
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={busy}
                                        className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 hover:text-white underline decoration-dotted underline-offset-4 w-full text-center"
                                        data-testid="courtroom-waitlist-submit"
                                    >
                                        {busy ? "..." : "Or join the free waitlist (no lock-in)"}
                                    </button>
                                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 mt-3 text-center">
                                        REFUNDABLE 30 DAYS · POWERED BY STRIPE · ZERO RISK
                                    </p>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* PROBLEM */}
            <section className="border-b-2 border-white/20" data-testid="courtroom-problem">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
                        / THE PROBLEM
                    </div>
                    <h2 className="font-anton text-3xl sm:text-5xl uppercase leading-none mb-8 max-w-4xl">
                        Mock-trial practice is{" "}
                        <span className="text-[#FF3B30]">$10K-$25K per case</span>. Lawyers skip it. Mistakes cost clients.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            {
                                t: "$10,000+",
                                d: "Cost of a single full mock trial with consultants. Most attorneys never get one in their career.",
                            },
                            {
                                t: "0 HOURS",
                                d: "Time most associates get for case-prep simulation before being dropped into a real courtroom.",
                            },
                            {
                                t: "97%",
                                d: "Of criminal cases end in plea deals — yet trial-ready lawyers settle smarter and win more.",
                            },
                        ].map((b, i) => (
                            <div key={i} className="border-2 border-white/20 p-6">
                                <div className="font-anton text-4xl text-[#FF3B30] leading-none mb-3">{b.t}</div>
                                <p className="font-sans text-sm text-white/70 leading-relaxed">{b.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="border-b-2 border-white/20" data-testid="courtroom-how">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
                        / HOW IT WORKS
                    </div>
                    <h2 className="font-anton text-3xl sm:text-5xl uppercase leading-none mb-10 max-w-4xl">
                        Pick a case. Pick a role. Go to trial.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 border-2 border-white/20">
                        {[
                            { n: "01", t: "PICK DIFFICULTY", d: "🟢 Easy → 🟠 Moderate → 🔴 Capital" },
                            { n: "02", t: "PICK YOUR ROLE", d: "Defense · Prosecution · Civil · Appellate" },
                            { n: "03", t: "10 BOTS ENGAGE", d: "Judge · Opposing · Witnesses · Jury · Expert · Bailiff · Clerk · Investigator · Forensic · Sentencing" },
                            { n: "04", t: "GET GRADED", d: "Transcript · Score · Improvement notes · CLE certificate*" },
                        ].map((s, i) => (
                            <div
                                key={s.n}
                                className={`p-6 ${i < 3 ? "border-r-2 border-white/20" : ""}`}
                            >
                                <div className="font-mono text-[10px] tracking-[0.3em] text-[#FF3B30] mb-2">
                                    {s.n}
                                </div>
                                <div className="font-anton text-xl uppercase leading-none mb-3">{s.t}</div>
                                <div className="font-sans text-xs text-white/60 leading-relaxed">{s.d}</div>
                            </div>
                        ))}
                    </div>
                    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40 mt-4">
                        * CLE CREDIT CERTIFICATES PENDING STATE BAR APPROVAL · LAUNCH-MARKET STATES FIRST
                    </p>
                </div>
            </section>

            {/* COMPARISON */}
            <section className="border-b-2 border-white/20" data-testid="courtroom-comparison">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
                        / WHY HPM COURTROOM VS. WHAT'S OUT THERE
                    </div>
                    <h2 className="font-anton text-3xl sm:text-5xl uppercase leading-none mb-10 max-w-4xl">
                        Existing platforms are <span className="text-white/40">study tools</span>.{" "}
                        <span className="text-[#FF3B30]">We're a courtroom.</span>
                    </h2>
                    <div className="border-2 border-white/20 overflow-x-auto">
                        <table className="w-full font-mono text-xs">
                            <thead>
                                <tr className="border-b-2 border-white/20 bg-white/5">
                                    <th className="text-left p-4 uppercase tracking-[0.25em] text-white/50 w-1/3">
                                        FEATURE
                                    </th>
                                    <th className="text-center p-4 uppercase tracking-[0.25em] text-white/50">
                                        Quimbee<br /><span className="text-[9px] tracking-[0.2em] text-white/40">$23-33/MO</span>
                                    </th>
                                    <th className="text-center p-4 uppercase tracking-[0.25em] text-white/50">
                                        BARBRI<br /><span className="text-[9px] tracking-[0.2em] text-white/40">$1,199+</span>
                                    </th>
                                    <th className="text-center p-4 uppercase tracking-[0.25em] text-[#FF3B30] bg-[#FF3B30]/5">
                                        HPM COURTROOM<br /><span className="text-[9px] tracking-[0.2em] text-[#FF3B30]">$49/MO</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    ["Case briefs + outlines", "yes", "yes", "yes"],
                                    ["Passive video lessons", "yes", "yes", "no"],
                                    ["Multiple choice practice", "no", "yes", "no"],
                                    ["LIVE AI opposing counsel", "no", "no", "yes"],
                                    ["LIVE AI judge rulings", "no", "no", "yes"],
                                    ["Graded trial transcripts", "no", "no", "yes"],
                                    ["10-bot adversarial roleplay", "no", "no", "yes"],
                                    ["Difficulty 🟢 → 🔴 capital", "no", "no", "yes"],
                                    ["CLE-credit certificates", "no", "yes", "pending"],
                                    ["Custom case upload", "no", "no", "yes"],
                                ].map((row, i) => (
                                    <tr
                                        key={`comparison-${row[0]}`}
                                        className={`border-b border-white/10 ${i % 2 === 1 ? "bg-white/[0.02]" : ""}`}
                                    >
                                        <td className="p-4 text-white/80">{row[0]}</td>
                                        <td className="text-center p-4">
                                            {row[1] === "yes" ? (
                                                <span className="text-[#00FF85]">✓</span>
                                            ) : row[1] === "pending" ? (
                                                <span className="text-[#FFCC00] text-[9px] tracking-[0.2em]">SOON</span>
                                            ) : (
                                                <span className="text-white/30">—</span>
                                            )}
                                        </td>
                                        <td className="text-center p-4">
                                            {row[2] === "yes" ? (
                                                <span className="text-[#00FF85]">✓</span>
                                            ) : row[2] === "pending" ? (
                                                <span className="text-[#FFCC00] text-[9px] tracking-[0.2em]">SOON</span>
                                            ) : (
                                                <span className="text-white/30">—</span>
                                            )}
                                        </td>
                                        <td className="text-center p-4 bg-[#FF3B30]/5">
                                            {row[3] === "yes" ? (
                                                <span className="text-[#00FF85] text-base">✓</span>
                                            ) : row[3] === "pending" ? (
                                                <span className="text-[#FFCC00] text-[9px] tracking-[0.2em]">SOON</span>
                                            ) : (
                                                <span className="text-white/30">—</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mt-4">
                        DATA AS OF 2026. SOURCES: PUBLIC PRICING ON QUIMBEE.COM, BARBRI.COM.
                    </p>
                </div>
            </section>

            {/* PRICING */}
            <section className="border-b-2 border-white/20" data-testid="courtroom-pricing">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
                        / FOUNDING MEMBER PRICING
                    </div>
                    <h2 className="font-anton text-3xl sm:text-5xl uppercase leading-none mb-10 max-w-4xl">
                        Lock <span className="text-[#FFCC00]">50% off forever</span> with a $99 founding deal.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 border-2 border-white/20">
                        {TIERS.map((t, i) => (
                            <div
                                key={t.key}
                                className={`p-6 ${i < 2 ? "border-r-2 border-white/20" : ""} ${t.featured ? "bg-white/5" : ""}`}
                                data-testid={`courtroom-tier-${t.key}`}
                            >
                                <div className="font-mono text-[10px] tracking-[0.3em] text-[#FF3B30] mb-2">
                                    {t.label}
                                </div>
                                <div className="font-anton text-5xl leading-none mb-1">
                                    ${t.price}
                                    <span className="text-white/40 text-sm ml-1">{t.unit}</span>
                                </div>
                                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#00FF85] mb-4">
                                    FOUNDING MEMBERS: ${Math.round(t.price * 0.5)}{t.unit} FOREVER
                                </div>
                                <ul className="space-y-2 mb-2">
                                    {t.bullets.map((b, j) => (
                                        <li
                                            key={j}
                                            className="flex items-start gap-2 font-sans text-sm text-white/75"
                                        >
                                            <CheckCircle2 size={12} className="text-[#00FF85] mt-1 shrink-0" />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section data-testid="courtroom-final-cta">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16 text-center">
                    <h2 className="font-anton text-3xl sm:text-6xl uppercase leading-none mb-4">
                        Be on the bench when it opens.
                    </h2>
                    <p className="font-sans text-white/70 max-w-2xl mx-auto mb-6">
                        The first 100 founding members lock $49/mo Solo or $99 lifetime presale. After
                        launch, prices double.
                    </p>
                    <a
                        href="#top"
                        onClick={(e) => {
                            e.preventDefault();
                            window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className="btn-brutal btn-brutal-red inline-flex text-[10px]"
                        data-testid="courtroom-scroll-top"
                    >
                        CLAIM MY FOUNDING SPOT → <ArrowRight size={12} />
                    </a>
                </div>
            </section>

            <Footer />
        </div>
    );
}
